// adapted from here: https://medium.com/javascript-in-plain-english/v-outside-click-writing-vue-directives-for-seamless-interactions-99abdb362813

import type { Directive, DirectiveBinding } from 'vue'

type ClickOutsideHandler = () => void

interface ClickOutsideObjectValue {
  handler?: ClickOutsideHandler
  exclude?: string[]
  disabled?: boolean
}

type ClickOutsideValue = ClickOutsideHandler | ClickOutsideObjectValue

type ClickOutsideElement = HTMLElement & { __clickOutsideKey?: string }

const EVENTS: Record<string, (ev: Event) => void> = {}

function validate(value: ClickOutsideValue | undefined): boolean {
  const validObj = !!value && typeof value === 'object'
  const validFn = !!value && typeof value === 'function'
  const validHandler =
    validFn ||
    (validObj &&
      typeof (value as ClickOutsideObjectValue).handler === 'function')
  const validExclude =
    validFn ||
    ((validObj && (value as ClickOutsideObjectValue).exclude
      ? Array.isArray((value as ClickOutsideObjectValue).exclude)
      : true) as boolean)
  if (!validObj && !validFn) {
    // eslint-disable-next-line
    console.error(
      '[clickOutside.js] Invalid binding: value should be a function or an object',
    )
    return false
  }
  if (!validHandler) {
    // eslint-disable-next-line
    console.error(
      '[clickOutside.js] Invalid binding: value.handler should be a function',
    )
    return false
  }
  if (!validExclude) {
    // eslint-disable-next-line
    console.error(
      '[clickOutside.js] Invalid binding: value.exclude should be an array or not be defined',
    )
    return false
  }
  return true
}

function randomKey(): string {
  return Math.random().toString(36).substr(2)
}

function bindClickOutside(
  el: ClickOutsideElement,
  binding: DirectiveBinding<ClickOutsideValue>,
): void {
  const value = binding.value
  if (value && typeof value === 'object' && value.disabled) {
    return
  }
  if (!validate(value)) {
    return
  }
  const key = `${Object.keys(el.dataset).join()}___${randomKey()}`
  if (el.__clickOutsideKey) {
    document.body.removeEventListener('click', EVENTS[el.__clickOutsideKey])
    document.body.removeEventListener(
      'touchstart',
      EVENTS[el.__clickOutsideKey],
    )
    delete EVENTS[el.__clickOutsideKey]
  }
  el.__clickOutsideKey = key
  EVENTS[key] = (ev: Event) => {
    ev.stopPropagation()
    const handler =
      typeof value === 'object' ? value.handler : (value as ClickOutsideHandler)
    const exclude =
      typeof value === 'object' ? value.exclude || [] : ([] as string[])

    const target = ev.target as HTMLElement | null
    let hasClickedOnExcluded = false
    if (target && target.className) {
      for (const className of exclude) {
        hasClickedOnExcluded = target.classList.contains(className)
        if (hasClickedOnExcluded) {
          break
        }
      }
    }

    if (
      (target && el.contains(target)) ||
      el === target ||
      hasClickedOnExcluded
    ) {
      return
    }

    handler?.()
  }

  document.body.addEventListener('click', EVENTS[key])
  document.body.addEventListener('touchstart', EVENTS[key])
}

function unbindClickOutside(el: ClickOutsideElement): void {
  const key = el.__clickOutsideKey
  if (key) {
    document.body.removeEventListener('click', EVENTS[key])
    document.body.removeEventListener('touchstart', EVENTS[key])
    delete EVENTS[key]
    delete el.__clickOutsideKey
  }
}

const ClickOutside: Directive<ClickOutsideElement, ClickOutsideValue> = {
  // Vue 3 hook names: bind→beforeMount, componentUpdated→updated, unbind→unmounted.
  beforeMount: bindClickOutside,
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      unbindClickOutside(el)
      bindClickOutside(el, binding)
    }
  },
  unmounted: unbindClickOutside,
}

export default ClickOutside
